// Prefix summaries for the Traffic Signs imagery prefixes (images/, camera-data/).
//
// Lifted out of ImageryUploadPanels (2026-09-09) because the counts are now
// needed in TWO places at once: inside the imagery modal, and as a readiness
// chip on the selected TSA task card. Two independent fetchers would drift
// apart the moment a user uploaded from the modal — the chip would keep showing
// the pre-upload count until something else happened to refetch. One hook, one
// source of truth, so an upload updates both.
import { useCallback, useEffect, useRef, useState } from 'react';
import fileService from '../../../services/fileService';

export const IMAGERY_TARGETS = [
  {
    key: 'images',
    prefix: 'images/',
    title: 'Camera images',
    hint: 'Frames named <run>_<ms>_C<n>.jpg; only frames listed by a registration CSV are used.',
    accept: '.jpg,.jpeg,.png,.tif,.tiff,.bmp',
    button: 'Upload Images',
  },
  {
    key: 'cameraData',
    prefix: 'camera-data/',
    title: 'Camera data',
    hint: '*_registration.csv (img_name + laz_file columns) plus the per-camera .cam / .cal / .4x4 / .eif files.',
    accept: '.csv,.cam,.cal,.4x4,.eif,.xlsx,.txt,.json,.xml',
    button: 'Upload Camera Data',
  },
];

/**
 * @param {object} args
 * @param {string|number|null} args.projectId
 * @param {string} args.sourceStorage  only 's3' projects have these prefixes
 * @param {(msg: string) => void} [args.onNotify]
 * @returns {{
 *   summaries: Record<string, {count: number, bytes: number}>,
 *   loading: Record<string, boolean>,
 *   fetchErrors: Record<string, string|null>,
 *   refresh: (prefix: string) => Promise<void>,
 *   enabled: boolean,
 * }}
 */
export default function useImagerySummaries({ projectId, sourceStorage, onNotify }) {
  const [summaries, setSummaries] = useState({});     // prefix -> {count, bytes}
  const [loading, setLoading] = useState({});         // prefix -> bool
  const [fetchErrors, setFetchErrors] = useState({}); // prefix -> message | null
  // Prefixes currently in a failed-fetch state, tracked outside React state so
  // a repeated poll that keeps failing doesn't re-fire onNotify every time —
  // only the transition into "failed" should surface a notification.
  const failedPrefixesRef = useRef(new Set());

  const enabled = sourceStorage === 's3' && Boolean(projectId);

  const refresh = useCallback(async (prefix) => {
    if (!projectId) return;
    setLoading((prev) => ({ ...prev, [prefix]: true }));
    try {
      const data = await fileService.getS3PrefixSummary(projectId, prefix);
      setSummaries((prev) => ({ ...prev, [prefix]: { count: data?.count || 0, bytes: data?.bytes || 0 } }));
      failedPrefixesRef.current.delete(prefix);
      setFetchErrors((prev) => (prev[prefix] ? { ...prev, [prefix]: null } : prev));
    } catch (err) {
      const message = err?.message || `Failed to get summary for ${prefix}`;
      console.warn(`[useImagerySummaries] summary failed for ${prefix}:`, message);
      const isNewFailure = !failedPrefixesRef.current.has(prefix);
      failedPrefixesRef.current.add(prefix);
      setFetchErrors((prev) => ({ ...prev, [prefix]: message }));
      if (isNewFailure && typeof onNotify === 'function') onNotify(message);
    } finally {
      setLoading((prev) => ({ ...prev, [prefix]: false }));
    }
  }, [projectId, onNotify]);

  useEffect(() => {
    if (!enabled) return;
    IMAGERY_TARGETS.forEach((t) => { refresh(t.prefix); });
  }, [enabled, refresh]);

  return { summaries, loading, fetchErrors, refresh, enabled };
}

/**
 * Readiness for the TSA card chip and the modal's own banner.
 *
 * `ready` is false while any prefix is still empty — that is exactly the state
 * the backend rejects at dispatch with 422 images_missing / camera_data_missing,
 * BEFORE the ledger debit. Showing it here turns that rejection into something
 * the user can see and fix before submitting.
 *
 * A prefix whose fetch FAILED is deliberately not counted as missing: we cannot
 * prove it is empty, and claiming "no imagery" over a transient network error
 * would send the user off to re-upload files that are already there.
 */
export function imageryReadiness(summaries, fetchErrors) {
  const missing = [];
  let unknown = false;
  IMAGERY_TARGETS.forEach((t) => {
    if (fetchErrors?.[t.prefix]) { unknown = true; return; }
    if (!(summaries?.[t.prefix]?.count > 0)) missing.push(t.title);
  });
  return { ready: missing.length === 0 && !unknown, missing, unknown };
}
